// Sound effects for Rounds, synthesised with the Web Audio API: no audio files, nothing to license, a few kilobytes.
// Unlocked by a user gesture (the join tap). Mute and volume are remembered on the device. On iPhones the audio
// session is "ambient": the silent switch is respected and the player's own audio (a stream, music) keeps playing.
// Phone speakers barely reproduce anything under about 200 Hz, so every low thump carries a harmonic and a click
// that a phone can play; headphones still get the body.
import { useSyncExternalStore } from 'react';

export type SfxName = 'tick' | 'heartbeat' | 'lock' | 'correct' | 'wrong' | 'whoosh' | 'drumroll' | 'fanfare' | 'ping' | 'charge' | 'deal' | 'pop' | 'streak' | 'timeup';

const BASE_GAIN = 0.55;
const read = (key: string): string | null => { try { return localStorage.getItem(key); } catch { return null; } };
const write = (key: string, value: string) => { try { localStorage.setItem(key, value); } catch { /* storage unavailable */ } };
const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1);

let muted = read('rounds.muted') === '1';
let volume = (() => { const v = read('rounds.volume'); return v === null ? 1 : clamp01(Number(v)); })();
const listeners = new Set<() => void>();
let ctx: AudioContext | undefined; let master: GainNode | undefined; let noiseBuf: AudioBuffer | undefined;
const lastPlayed = new Map<SfxName, number>();

function ensure(): AudioContext | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return undefined;
    // Safari 16.4+: mix with other audio and follow the silent switch (set before the context exists).
    try { const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession; if (session) session.type = 'ambient'; } catch { /* not supported */ }
    ctx = new AC();
    const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -14; comp.ratio.value = 4; comp.connect(ctx.destination);
    master = ctx.createGain(); master.gain.value = BASE_GAIN * volume; master.connect(comp);
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate); const d = noiseBuf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void ctx?.resume(); });
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

// --- tiny synthesis helpers -------------------------------------------------
function tone(c: AudioContext, at: number, o: { freq: number; to?: number; type?: OscillatorType; dur: number; gain?: number; attack?: number; filter?: number }) {
  const osc = c.createOscillator(); const g = c.createGain(); let out: AudioNode = g;
  osc.type = o.type ?? 'sine'; osc.frequency.setValueAtTime(o.freq, at); if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, at + o.dur);
  const peak = o.gain ?? 0.4; const a = o.attack ?? 0.005;
  g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(peak, at + a); g.gain.exponentialRampToValueAtTime(0.0001, at + o.dur);
  osc.connect(g);
  if (o.filter) { const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = o.filter; g.connect(f); out = f; }
  out.connect(master!); osc.start(at); osc.stop(at + o.dur + 0.05);
}
function noise(c: AudioContext, at: number, o: { dur: number; gain?: number; type?: BiquadFilterType; freq: number; to?: number; q?: number }) {
  const src = c.createBufferSource(); src.buffer = noiseBuf!; const f = c.createBiquadFilter(); const g = c.createGain();
  f.type = o.type ?? 'bandpass'; f.frequency.setValueAtTime(o.freq, at); if (o.to) f.frequency.exponentialRampToValueAtTime(o.to, at + o.dur); f.Q.value = o.q ?? 1;
  const peak = o.gain ?? 0.3; g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(peak, at + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, at + o.dur);
  src.connect(f); f.connect(g); g.connect(master!); src.start(at, Math.random() * 0.5); src.stop(at + o.dur + 0.05);
}
/** A low hit that still reads on a phone speaker: a sine body, its second harmonic and a soft click. */
function thump(c: AudioContext, at: number, o: { freq: number; to: number; dur: number; gain: number }) {
  tone(c, at, { freq: o.freq, to: o.to, dur: o.dur, gain: o.gain });
  tone(c, at, { freq: o.freq * 2, to: o.to * 2, type: 'triangle', dur: o.dur * 0.8, gain: o.gain * 0.42, filter: 900 });
  noise(c, at, { dur: 0.03, freq: 1400, type: 'lowpass', q: 0.7, gain: o.gain * 0.16 });
}
const NOTE = (n: number) => 440 * Math.pow(2, (n - 69) / 12); // MIDI note to Hz

const RECIPES: Record<SfxName, (c: AudioContext, t: number) => void> = {
  // A soft wooden tick, not a beep: it plays every second for up to half a minute.
  tick: (c, t) => { tone(c, t, { freq: 1500, to: 1100, type: 'triangle', dur: 0.035, gain: 0.1, filter: 3800 }); noise(c, t, { dur: 0.018, freq: 3200, q: 2, gain: 0.04 }); },
  // Lub-dub, all over in 0.4 s so it fits inside each of the last five seconds.
  heartbeat: (c, t) => { thump(c, t, { freq: 96, to: 52, dur: 0.17, gain: 0.85 }); thump(c, t + 0.19, { freq: 84, to: 46, dur: 0.15, gain: 0.55 }); },
  lock: (c, t) => { tone(c, t, { freq: 720, to: 320, dur: 0.09, gain: 0.35 }); noise(c, t, { dur: 0.04, freq: 3000, gain: 0.12, type: 'highpass' }); },
  correct: (c, t) => {
    [72, 76, 79, 84].forEach((n, i) => tone(c, t + i * 0.075, { freq: NOTE(n), type: 'triangle', dur: 0.32, gain: 0.32 }));
    tone(c, t + 0.3, { freq: 2600, to: 4200, dur: 0.25, gain: 0.06 }); tone(c, t + 0.36, { freq: 3100, to: 5200, dur: 0.22, gain: 0.05 });
  },
  wrong: (c, t) => { tone(c, t, { freq: 233, to: 220, type: 'sawtooth', dur: 0.22, gain: 0.22, filter: 700 }); tone(c, t + 0.22, { freq: 175, to: 140, type: 'sawtooth', dur: 0.42, gain: 0.22, filter: 600 }); },
  whoosh: (c, t) => { noise(c, t, { dur: 0.45, freq: 300, to: 3200, q: 0.8, gain: 0.22 }); },
  drumroll: (c, t) => {
    for (let i = 0; i < 36; i++) noise(c, t + i * 0.05, { dur: 0.06, freq: 900 + i * 15, type: 'bandpass', q: 0.7, gain: 0.06 + i * 0.006 });
    noise(c, t + 1.85, { dur: 1.2, freq: 5000, type: 'highpass', gain: 0.28 }); thump(c, t + 1.85, { freq: 80, to: 42, dur: 0.4, gain: 0.7 });
  },
  fanfare: (c, t) => {
    const brass = (n: number, at: number, dur: number) => { tone(c, at, { freq: NOTE(n), type: 'sawtooth', dur, gain: 0.16, attack: 0.03, filter: 2200 }); tone(c, at, { freq: NOTE(n) * 1.003, type: 'sawtooth', dur, gain: 0.12, attack: 0.03, filter: 1800 }); };
    brass(67, t, 0.16); brass(67, t + 0.18, 0.16); brass(67, t + 0.36, 0.16); [60, 64, 67, 72].forEach((n) => brass(n, t + 0.56, 0.9));
  },
  ping: (c, t) => { tone(c, t, { freq: 1320, dur: 0.45, gain: 0.25 }); tone(c, t, { freq: 2640, dur: 0.3, gain: 0.08 }); },
  // The defibrillator charging (a rising whine), then the shock.
  charge: (c, t) => { tone(c, t, { freq: 380, to: 2600, dur: 0.95, gain: 0.12, attack: 0.05 }); thump(c, t + 1.0, { freq: 110, to: 45, dur: 0.25, gain: 0.8 }); noise(c, t + 1.0, { dur: 0.15, freq: 1200, gain: 0.2 }); },
  deal: (c, t) => { noise(c, t, { dur: 0.05, freq: 4200, type: 'highpass', gain: 0.18 }); tone(c, t, { freq: 520, to: 880, dur: 0.06, gain: 0.08 }); },
  pop: (c, t) => { tone(c, t, { freq: 880, to: 1700, dur: 0.07, gain: 0.25 }); },
  streak: (c, t) => { noise(c, t, { dur: 0.5, freq: 600, to: 2400, q: 1.5, gain: 0.16 }); [79, 84, 88].forEach((n, i) => tone(c, t + 0.12 + i * 0.06, { freq: NOTE(n), type: 'triangle', dur: 0.2, gain: 0.16 })); },
  timeup: (c, t) => { tone(c, t, { freq: 330, type: 'square', dur: 0.25, gain: 0.1, filter: 1200 }); tone(c, t + 0.28, { freq: 262, type: 'square', dur: 0.35, gain: 0.1, filter: 1000 }); },
};

export const sfx = {
  /** Call from a user gesture (the join tap) so browsers allow audio. Older iPhones also need a sound started in it. */
  unlock(): void {
    const c = ensure(); if (!c) return;
    if (c.state === 'suspended') void c.resume();
    try { const s = c.createBufferSource(); s.buffer = c.createBuffer(1, 1, c.sampleRate); s.connect(c.destination); s.start(0); } catch { /* audio not available */ }
  },
  play(name: SfxName): void {
    if (muted || volume === 0) return; const c = ensure(); if (!c || !master) return;
    const now = performance.now(); if ((lastPlayed.get(name) ?? -Infinity) > now - 60) return; lastPlayed.set(name, now);
    try { RECIPES[name](c, c.currentTime + 0.01); } catch { /* audio not available */ }
  },
  isMuted: () => muted,
  setMuted(m: boolean) { muted = m; write('rounds.muted', m ? '1' : '0'); listeners.forEach((l) => l()); },
  /** Master volume, 0 to 1 (default 1), remembered on the device. Mute is separate and wins. */
  volume: () => volume,
  setVolume(v: number) {
    volume = clamp01(v); write('rounds.volume', String(volume));
    if (ctx && master) master.gain.setTargetAtTime(BASE_GAIN * volume, ctx.currentTime, 0.02);
    listeners.forEach((l) => l());
  },
  names: Object.keys(RECIPES) as SfxName[],
};

export function useMuted(): [boolean, (m: boolean) => void] {
  const m = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => muted, () => muted);
  return [m, sfx.setMuted];
}
